import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { getMergedUserData } from "../pages/const";
import {
  postCancelOrder,
  isErpApiSuccess,
  generateIdempotencyKey,
  getErpRequestErrorMessage,
} from "../erpClient";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";

import Select from "react-select";

const AUTO_TYPE_PAY_MODES = ["cash", "wallet", "kasar", "old_gold", "old_silver"];

const findBankAccountForPayMode = (payMode, accounts = []) => {
  if (!payMode) return null;
  const keyword = String(payMode).toLowerCase().replace(/_/g, " ");
  return (
    accounts.find((bank) => {
      const accountType = String(
        bank.account_type || bank.accountType || "",
      ).toLowerCase();
      const bankName = String(bank.bank_name || bank.bankName || "").toLowerCase();
      return (
        accountType === keyword ||
        accountType === String(payMode).toLowerCase() ||
        bankName.includes(keyword)
      );
    }) || null
  );
};

const resolveBankAccountId = (typeValue) => {
  if (typeValue === null || typeValue === undefined || typeValue === "") {
    return null;
  }
  const parsed = parseInt(String(typeValue), 10);
  if (
    !Number.isNaN(parsed) &&
    parsed > 0 &&
    String(parsed) === String(typeValue).trim()
  ) {
    return parsed;
  }
  return null;
};

const parseMoney = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const numeric = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

const getOrderReceivedAmount = (order = {}) => {
  const explicit =
    order.adv_paid ??
    order.advance_paid ??
    order.advPaid ??
    order.rec_amount ??
    order.rec_amt ??
    order.paid_amount ??
    order.advance;
  if (explicit != null && explicit !== "") {
    return parseMoney(explicit);
  }

  const payments = Array.isArray(order.payments)
    ? order.payments
    : Array.isArray(order.payment_details)
      ? order.payment_details
      : [];

  return payments.reduce(
    (sum, payment) =>
      sum +
      parseMoney(
        payment.aavak_amt ??
          payment.amount ??
          payment.amt ??
          payment.total_amount ??
          0,
      ),
    0,
  );
};

const resolveRowBankAccountId = (row, accounts = []) => {
  const fromType = resolveBankAccountId(row?.type);
  if (fromType) return fromType;
  const account = findBankAccountForPayMode(row?.pay_mode, accounts);
  return account?.id != null ? Number(account.id) : null;
};

// Payment method options
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "Wallet", label: "Wallet" },
  { value: "old_gold", label: "Old Gold" },
  { value: "old_silver", label: "Old Silver" },
  { value: "kasar", label: "Kasar" },
  { value: "scheme", label: "Scheme" },
  { value: "order", label: "Order" },
  { value: "os", label: "OS" },
];

function CancelOrderModal({
  show,
  onHide,
  orderData,
  selectedItems = [],
  isWholeOrder = false,
  orderType = "custom", // "custom" or "repairing"
  bankAccountsList = [], // Add bank accounts list prop
  onSuccess,
}) {
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const [cancelStep, setCancelStep] = useState(1);
  const [cancelReason, setCancelReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cancelDate, setCancelDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [cancelPaymentRows, setCancelPaymentRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 2,
      pay_mode: "cheque",
      type: "",
      remarks: "",
      amount: "0.00",
    },
    {
      id: 3,
      pay_mode: "bank_transfer",
      type: "",
      remarks: "",
      amount: "0.00",
    },
  ]);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const userData = getMergedUserData();

  // Listen for API URL changes and close modal
  React.useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in CancelOrderModal, closing modal...', event.detail);
      if (show) {
        toast('Database switched. Closing modal.');
        onHide();
      }
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [show, onHide]);

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (show && orderData) {
      setCancelStep(1);
      setCancelReason("");
      setCancelDate(new Date().toISOString().split("T")[0]);
      setCancelRemarks("");
      setCancelPaymentRows([
        {
          id: 1,
          pay_mode: "cash",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 2,
          pay_mode: "cheque",
          type: "",
          remarks: "",
          amount: "0.00",
        },
        {
          id: 3,
          pay_mode: "bank_transfer",
          type: "",
          remarks: "",
          amount: "0.00",
        },
      ]);
    }
  }, [show, orderData]);

  React.useEffect(() => {
    if (!show || !bankAccountsList?.length) return;

    setCancelPaymentRows((prevRows) => {
      let changed = false;
      const nextRows = prevRows.map((row) => {
        if (row.type) return row;
        if (!AUTO_TYPE_PAY_MODES.includes(row.pay_mode)) return row;
        const account = findBankAccountForPayMode(row.pay_mode, bankAccountsList);
        if (!account?.id) return row;
        changed = true;
        return { ...row, type: String(account.id) };
      });
      return changed ? nextRows : prevRows;
    });
  }, [show, bankAccountsList]);

  // Handle payment row changes
  const handleCancelPaymentRowChange = (rowId, field, value) => {
    setCancelPaymentRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id !== rowId) return row;

        const updatedRow = { ...row, [field]: value };
        if (field === "pay_mode" && AUTO_TYPE_PAY_MODES.includes(value)) {
          const account = findBankAccountForPayMode(value, bankAccountsList);
          updatedRow.type = account?.id != null ? String(account.id) : "";
        }
        return updatedRow;
      }),
    );
  };

  // Calculate payment total
  const cancelPaymentTotal = cancelPaymentRows.reduce((sum, row) => {
    const value = parseFloat(row.amount);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const orderReceivedAmount = getOrderReceivedAmount(orderData || {});
  const hasUncollectedPayout =
    cancelPaymentTotal > 0.009 && orderReceivedAmount <= 0.009;

  const resolveByUser = () =>
    userData?.id ?? userData?.ua_id ?? userData?.user_id ?? null;

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    if (hasUncollectedPayout) {
      toast.error(
        "Paid amount is 0. Cash or any other payout cannot be saved because nothing was received on this order.",
      );
      return;
    }

    if (cancelPaymentTotal - orderReceivedAmount > 0.009) {
      toast.error(
        `Payout cannot be more than the paid amount (${orderReceivedAmount.toFixed(2)}).`,
      );
      return;
    }

    const byUser = resolveByUser();
    if (!byUser) {
      toast.error("User not logged in");
      return;
    }

    setIsSubmitting(true);
    try {
      // Get payment information
      const firstPaymentRow = cancelPaymentRows.find(
        (row) => parseFloat(row.amount || 0) > 0
      );
      const payMethod = firstPaymentRow?.pay_mode || "";
      const payAmount = parseFloat(firstPaymentRow?.amount || 0);
      const bankAccountId = firstPaymentRow
        ? resolveRowBankAccountId(firstPaymentRow, bankAccountsList)
        : null;

      if (payAmount > 0 && !bankAccountId) {
        toast.error("Please select a valid cash/bank account for refund");
        return;
      }

      const pageSource =
        orderType === "repairing" ? "repairing_order" : "custom_order";
      const orderId = orderData.id;
      const hasSelectedItems =
        !isWholeOrder &&
        Array.isArray(selectedItems) &&
        selectedItems.length > 0;

      const selectedItemIds = hasSelectedItems
        ? selectedItems
          .map((item) => item?.id)
          .filter((id) => id !== null && id !== undefined)
        : [];

      const selectedTagNos = hasSelectedItems
        ? selectedItems
          .map((item) => item?.tag_no)
          .filter((tag) => tag !== null && tag !== undefined && tag !== "")
        : [];

      // Prepare payload based on cancellation type
      const payload = {
        page_source: pageSource,
        order_id: orderId,
        reason: cancelReason.trim(),
        date: cancelDate,
        pay_method: payMethod,
        pay_amount: payAmount,
        remarks: cancelRemarks || "",
        by_user: byUser,
      };

      if (selectedItemIds.length > 0) {
        payload.item_ids = selectedItemIds;
      }

      if (selectedTagNos.length > 0) {
        payload.tag_no = selectedTagNos.join(",");
      }

      if (isWholeOrder) {
        payload.is_whole_order = true;
        payload.cancel_order_entries = true;

        const orderEntries =
          orderType === "repairing"
            ? orderData?.repairing_order_entry
            : orderData?.custom_order_entry;
        if (Array.isArray(orderEntries)) {
          const existingTagNos = orderEntries
            .map((item) => item?.tag_no)
            .filter((tag) => tag);
          if (existingTagNos.length > 0) {
            payload.tag_no = existingTagNos.join(",");
          }
        }
      }

      if (payAmount > 0 && bankAccountId) {
        payload.bank_account_id = bankAccountId;
        payload.bank_type = bankAccountId;
      }

      const needsRefundIdempotency = payAmount > 0;
      const idempotencyKey = needsRefundIdempotency
        ? generateIdempotencyKey()
        : undefined;
      if (idempotencyKey) {
        payload.idempotency_key = idempotencyKey;
      }

      const cancelData = await postCancelOrder(payload, { idempotencyKey });

      if (isErpApiSuccess(cancelData) || cancelData?.message === "radha") {
        toast.success(
          isWholeOrder
            ? "Order cancelled successfully"
            : "Selected items cancelled successfully",
        );
        setCancelReason("");
        setCancelRemarks("");
        onHide();
        if (onSuccess) onSuccess();
      } else {
        toast.error(
          cancelData?.message || "Failed to cancel order. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error(
        getErpRequestErrorMessage(
          error,
          "Failed to cancel order. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setCancelReason("");
    setCancelRemarks("");
    setCancelStep(1);
    onHide();
  };

  if (!orderData) return null;

  const orderNumber =
    orderData.co_number ||
    orderData.token_no ||
    orderData.order_number ||
    orderData.id ||
    "N/A";
  const orderTotalAmount = parseMoney(
    orderData.grand_total ??
      orderData.final_amount ??
      orderData.total_amount ??
      orderData.final_amt ??
      orderData.total ??
      0,
  );
  const bankTypeOptions = (bankAccountsList || []).map((bank) => ({
    value: String(bank.id),
    label: bank.bank_name || bank.account_number || `Bank ${bank.id}`,
  }));
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
      ...base,
      minHeight: "38px",
      borderColor: state.isFocused ? "var(--theme-primary)" : "#dee2e6",
      boxShadow: "none",
      borderRadius: "0.25rem",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 10px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
      contentClassName={cardClass}
    >
      <Modal.Header closeButton className="border-bottom py-3 px-4">
        <div className="d-flex align-items-center justify-content-between w-100 me-2">
          <div>
            <Modal.Title className="fs-5 fw-semibold mb-0">
              {isWholeOrder ? "Cancel Order" : "Cancel Selected Items"}
            </Modal.Title>
            <div className="text-muted small mt-1">Order {orderNumber}</div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {[1, 2].map((step, index) => (
              <div key={step} className="d-flex align-items-center gap-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 28,
                    height: 28,
                    fontSize: 13,
                    fontWeight: 600,
                    backgroundColor:
                      cancelStep >= step ? "var(--theme-primary)" : "#e9ecef",
                    color: cancelStep >= step ? "#fff" : "#6c757d",
                  }}
                >
                  {step}
                </div>
                {index === 0 && (
                  <div
                    style={{
                      width: 48,
                      height: 2,
                      backgroundColor:
                        cancelStep > 1 ? "var(--theme-primary)" : "#e9ecef",
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className={`border ${buttonClass} mb-4`}>
          <div className="row g-0">
            <div className="col-md-4 p-3 border-end">
              <div className="text-muted small">Total amount</div>
              <div className="fw-semibold mt-1">
                {formatIndianRupee(orderTotalAmount)}
              </div>
            </div>
            <div className="col-md-4 p-3 border-end">
              <div className="text-muted small">Paid amount</div>
              <div className="fw-semibold mt-1">
                {formatIndianRupee(orderReceivedAmount)}
              </div>
            </div>
            <div className="col-md-4 p-3">
              <Form.Label className="fw-semibold small mb-1">Date</Form.Label>
              <Form.Control
                type="date"
                className={buttonClass}
                value={cancelDate}
                onChange={(e) => setCancelDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {cancelStep === 1 && (
          <>
            <h6 className="fw-semibold text-primary mb-3">1. Reason</h6>
            {selectedItems.length > 0 && (
              <div className={`border ${buttonClass} px-3 py-2 mb-3`}>
                {selectedItems.map((item, idx) => (
                  <div key={item.id || idx} className="small py-1">
                    <span className="fw-semibold text-dark">
                      {item.tag_no ||
                        item.product_group_name ||
                        `Item ${idx + 1}`}
                    </span>
                    {item.product_group_name && item.tag_no && (
                      <span className="text-muted ms-2">
                        {item.product_group_name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Form.Group>
              <Form.Label className="fw-semibold small">
                Reason for cancellation
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Why is this order being cancelled?"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className={buttonClass}
              />
            </Form.Group>
          </>
        )}

        {cancelStep === 2 && (
          <>
        <h6 className="fw-semibold text-primary mb-3">2. Refund</h6>
        <div className={`table-responsive border ${cardClass} mb-3`}>
          <table className={`table mb-0 align-middle ${tableClass}`}>
            <thead className="text-dark">
              <tr>
                <th style={{ width: "48px" }}>#</th>
                <th>Mode</th>
                <th style={{ width: "28%" }}>Type</th>
                <th>Remarks</th>
                <th className="text-end" style={{ width: "120px" }}>
                  Amt
                </th>
              </tr>
            </thead>
            <tbody>
              {cancelPaymentRows.map((row, index) => (
                <tr key={row.id}>
                  <td>{index + 1}</td>
                  <td>
                    <Select
                      options={PAYMENT_METHODS}
                      value={
                        PAYMENT_METHODS.find(
                          (method) => method.value === row.pay_mode,
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        handleCancelPaymentRowChange(
                          row.id,
                          "pay_mode",
                          selectedOption?.value || "",
                        )
                      }
                      placeholder="Mode"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      isSearchable={false}
                      classNamePrefix="react-select"
                    />
                  </td>
                  <td>
                    <Select
                      options={bankTypeOptions}
                      value={
                        bankTypeOptions.find(
                          (option) => option.value === String(row.type || ""),
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        handleCancelPaymentRowChange(
                          row.id,
                          "type",
                          selectedOption?.value || "",
                        )
                      }
                      placeholder="Type"
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                      isSearchable
                      classNamePrefix="react-select"
                      noOptionsMessage={() => "No accounts"}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      placeholder="Remarks"
                      value={row.remarks}
                      className={buttonClass}
                      onChange={(e) =>
                        handleCancelPaymentRowChange(
                          row.id,
                          "remarks",
                          e.target.value,
                        )
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={row.amount}
                      className={`text-end ${buttonClass}`}
                      onChange={(e) =>
                        handleCancelPaymentRowChange(
                          row.id,
                          "amount",
                          e.target.value,
                        )
                      }
                      onWheel={(e) => e.target.blur()}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="text-muted small">Refund total</span>
          <span className="fw-semibold">
            {formatIndianRupee(cancelPaymentTotal)}
          </span>
        </div>

        {hasUncollectedPayout && (
          <div className="small text-danger mb-3">
            Paid amount is 0, so this refund cannot be saved.
          </div>
        )}

        <Form.Group>
          <Form.Label className="fw-semibold small">Remarks</Form.Label>
          <Form.Control
            type="text"
            placeholder="Optional note for this cancellation"
            value={cancelRemarks}
            onChange={(e) => setCancelRemarks(e.target.value)}
            className={buttonClass}
          />
        </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top py-2 px-4 d-flex justify-content-end gap-2">
        {cancelStep === 1 ? (
          <Button
            variant="primary"
            className={buttonClass}
            onClick={() => {
              if (!cancelReason.trim()) {
                toast.error("Please provide a reason for cancellation");
                return;
              }
              setCancelStep(2);
            }}
            disabled={!cancelReason.trim()}
          >
            Next
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              className={buttonClass}
              onClick={() => setCancelStep(1)}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <Button
              variant="primary"
              className={buttonClass}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Cancelling..." : "Confirm Cancel"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default CancelOrderModal;
